/**
 * The platform's mail server.
 *
 * The fields live in SmtpFields, shared with the school version. What stays
 * here is the part that is the platform's alone: the master "send email
 * notifications" switch, and the indicator saying which transport is actually
 * in use - without which an administrator filling this form in while
 * TC_SMTP_* is set in the environment has no way to tell their mail is still
 * going out through the environment.
 *
 * The test button sends a real message to the signed-in administrator and shows
 * whatever the mail server said, unedited. Someone with a wrong port needs
 * "Connection refused", not "something went wrong".
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import type { SiteEmailSettings as SiteEmail } from '../../lib/api/client'
import { Button, Checkbox, Pill, Spinner } from '../../components/ui'
import { Icon } from '../../components/icons'
import { toast } from '../../components/toast'
import { SmtpFields, TestSend, draftFrom, portOf } from './SmtpFields'
import type { SmtpDraft } from './SmtpFields'
import './email.css'

const SITE_EMAIL_KEY = ['platform', 'email', 'settings']

export function SiteSmtpSettings() {
  const settings = useQuery({ queryKey: SITE_EMAIL_KEY, queryFn: api.siteEmailSettings })

  if (settings.isLoading) return <Spinner label="Loading email settings" />
  if (!settings.data) return null

  // Remounted when the saved values change, so the draft always starts from
  // what the server holds rather than from a stale render.
  return <SmtpForm key={JSON.stringify(settings.data)} settings={settings.data} />
}

function SmtpForm({ settings }: { settings: SiteEmail }) {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<SmtpDraft | null>(null)
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [clearPassword, setClearPassword] = useState(false)

  const value = draft ?? draftFrom(settings)
  const emailEnabled = enabled ?? settings.email_enabled
  const set = <K extends keyof SmtpDraft>(key: K, next: SmtpDraft[K]) =>
    setDraft({ ...value, [key]: next })

  const { port, valid: portValid } = portOf(value)
  const dirty = draft !== null || clearPassword || enabled !== null

  const save = useMutation({
    mutationFn: () =>
      api.updateSiteEmailSettings({
        email_enabled: emailEnabled,
        smtp_host: value.smtp_host,
        smtp_port: port,
        smtp_username: value.smtp_username,
        smtp_password: value.smtp_password ? value.smtp_password : clearPassword ? '' : undefined,
        smtp_starttls: value.smtp_starttls,
        smtp_from_email: value.smtp_from_email,
        smtp_from_name: value.smtp_from_name,
        smtp_reply_to: value.smtp_reply_to,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(SITE_EMAIL_KEY, updated)
      setDraft(null)
      setEnabled(null)
      setClearPassword(false)
      toast('Email settings saved.')
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  const test = useMutation({
    mutationFn: (to: string) => api.sendTestEmail(to),
    // A refused test is not a failed request - the server answered, and the
    // answer is bad news. Both outcomes arrive through onSuccess.
    onSuccess: (result) => toast(result.message, result.ok ? 'info' : 'error'),
    onError: (error: Error) => toast(error.message, 'error'),
  })

  const discard = () => {
    setDraft(null)
    setEnabled(null)
    setClearPassword(false)
  }

  return (
    <section className="tc-email-panel" aria-labelledby="site-smtp-title">
      <header className="tc-email-heading">
        <span className="tc-email-icon" aria-hidden="true">
          <Icon.settings size={20} />
        </span>
        <div>
          <span className="tc-email-eyebrow">Platform policy</span>
          <h2 id="site-smtp-title">Mail server</h2>
          <p>
            The server every notification is sent through, unless a school has been given its own.
          </p>
        </div>
        <SourcePill source={settings.active_source} enabled={settings.email_enabled} />
      </header>

      <div className="tc-email-body">
        <Checkbox
          label="Send email notifications"
          checked={emailEnabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <p className="tc-email-note">
          With this off, nothing is emailed. Papers, reviews and printing all carry on as normal —
          the in-app queues are the record, and email is the reminder on top.
        </p>

        <SmtpFields
          value={value}
          set={set}
          passwordConfigured={settings.smtp_password_configured}
          clearPassword={clearPassword}
          onClearPassword={setClearPassword}
        />

        <div className="tc-email-actions">
          <Button
            variant="primary"
            disabled={!dirty || !portValid || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save mail settings'}
          </Button>
          <Button variant="secondary" disabled={!dirty || save.isPending} onClick={discard}>
            Discard changes
          </Button>
        </div>

        <TestSend dirty={dirty} pending={test.isPending} onSend={(to) => test.mutate(to)} />
        <p className="tc-email-note">
          The test uses the settings as saved, not what is on screen. Save first, then test.
        </p>
      </div>
    </section>
  )
}

/** Which transport is actually in play. */
function SourcePill({ source, enabled }: { source: string; enabled: boolean }) {
  if (source === 'site') return <Pill tone="success">Sending through these settings</Pill>
  if (source === 'environment')
    return (
      <Pill tone="warning">
        {enabled ? 'Falling back to server environment' : 'Sending through server environment'}
      </Pill>
    )
  return <Pill tone="secondary">No mail server yet</Pill>
}
